import 'webrtc-adapter'
import {debounce, deferred} from "../utils";
import Store from "../utils/store/store";

export const SHORT_DISCONNECT_TIMEOUT = 5000;
export const TIME_VIDEO_GLITCH = 2000
export const HEARD_BEAT_TIME = 3000
export const MAX_RESTART = 10

const default_video_state = {
  /* unprepared >  */
  status: 'unprepared',
  error: null,
  timerHeardBeat: null,
  timerDisconnect: null,
  isSubscribe: false,
  trackingMode: 'frame',
  lastFrame: 0,
  availabilityVideo: false,
  stream: null,
  deviceId: null,
  $video: {},
}

class _video extends Store {
  static inject = ["Game"];

  GameDfd = deferred("Game");

  unsubscribeGameSocket = null

  makingOffer = false;   // Показывает, что мы делаем offer
  polite = false;        // Определяем, считаем ли мы себя "вежливыми"

  // --- Массив для временного хранения ICE-кандидатов (до remoteDescription):
  pendingCandidates = [];

  // --- Флаг, чтобы не делать multiple restartRTCPeer подряд:
  restartPending = false;

  constructor(ctx, data, urls = window.URLS) {
    super(ctx)
    this.urls = urls;

    // В PLAYER_NUMBER будет 0 или 1,
    // 0 -> this.polite = true, 1 -> false
    this.polite = !window.PLAYER_NUMBER;

    this.set('devices', [])
    this.set('restart', 0)
    this.set('muted', false)

    this.resetState('localMain', true)
    this.resetState('localExtra', true)
    this.resetState('remoteMain')
    this.resetState('remoteExtra')
  }

  injectDependencies({Game}) {
    this.GameDfd.resolve(Game);
  }

  update(name, data) {
    this.set(name, {...this.get(name), ...data})
  }

  onMessageSocker = (event) => {
    switch (event.type) {
      case "call_offer":
        if (event.message.caller !== window.PLAYER_ID) {
          this.handlerOfferVideo(event.message.payload);
        }
        break;
      case "call_answer":
        if (event.message.caller !== window.PLAYER_ID) {
          this.handlerAnswerVideo(event.message.payload);
        }
        break;
      case "call_candidate":
        if (event.message.caller !== window.PLAYER_ID) {
          this.handlerCandidateVideo(event.message.payload);
        }
        break;
    }
  };

  async createRTCPeer($localMainVideo, $localExtraVideo, $remoteMainVideo, $remoteExtraVideo) {
    window.V = this;

    if ($localMainVideo.current) {
      this.update('localMain', {$video: $localMainVideo})
    }
    if ($localExtraVideo.current) {
      this.update('localExtra', {$video: $localExtraVideo})
    }
    if ($remoteMainVideo.current) {
      this.update('remoteMain', {$video: $remoteMainVideo})
    }
    if ($remoteExtraVideo.current) {
      this.update('remoteExtra', {$video: $remoteExtraVideo})
    }

    if (!this.unsubscribeGameSocket) {
      const Game = await this.GameDfd.promise();
      this.unsubscribeGameSocket = Game.subscribeSocketEvent(this.onMessageSocker);
    }

    try {
      this.pc = new RTCPeerConnection({
        iceServers: [{urls: "stun:stun.l.google.com:19302"}, {urls: "stun:stun1.l.google.com:19302"}, {urls: "stun:stun2.l.google.com:19302"}, {urls: "stun:stun3.l.google.com:19302"},],
      });

      this.pc.addEventListener('signalingstatechange', () => {
        // console.log('signalingState', this.pc.signalingState);
      })

      this.pc.addEventListener("connectionstatechange", () => {
        // console.log('connectionState', this.pc.connectionState);
      });

      // negotiationneeded
      this.pc.addEventListener("negotiationneeded", async () => {
        // console.log('negotiationneeded', this.pc.signalingState);
        // <-- ADDED/CHANGED: проверяем состояние, чтобы не создавать offer, если не stable
        await this.createVideoOffer();
      });

      // icecandidate
      this.pc.addEventListener("icecandidate", async (e) => {
        if (!e.candidate) return;
        const Game = await this.GameDfd.promise();
        Game.socketSend({
          type: "call_candidate", message: {
            caller: window.PLAYER_ID, payload: e.candidate,
          },
        });
      });

      // track
      this.pc.addEventListener("track", this.handlerSetRemoteTrack);

    } catch (e) {
      console.error("Cannot create RTCPeerConnection object, error:", e);
      // не сохраняется
      setTimeout(() => {
        this.restartRTCPeer();
        //   TODO: хз что с этим делать
      }, 3000);
      return;
    }

    // Создаём локальный стрим
    try {
      if (!await this.checkPossibilities()) {
        throw new Error('Нету доступа к камере')
      }
      const {$video, deviceId} = this.get('localMain')
      const localMainStream = await this.createLocalStream('localMain', {
        facingMode: "environment",
        deviceId: {ideal: deviceId}
      }, true);
      $video.current.srcObject = localMainStream;
      const {deviceID} = localMainStream.getVideoTracks()[0].getSettings()
      localMainStream.getTracks().forEach((track) => {
        const sender = this.pc.addTrack(track, localMainStream);
        sender.label = 'localMain'
      });
      this.update('localMain', {
        stream: localMainStream,
        status: 'connect',
        deviceId,
      })
      this.monitoringVideo('localMain')

      if (this.get('localExtra').$video.current) {
        const localExtraStream = await this.createLocalExtraStreamByMain(deviceId)
        if (localExtraStream) {
          this.get('localExtra').$video.current.srcObject = localExtraStream;
          localExtraStream.getTracks().forEach((track) => {
            const sender = this.pc.addTrack(track, localExtraStream);
            sender.label = 'localExtra'
          });
        }
        this.update('localExtra', {
          stream: localExtraStream,
          status: localExtraStream ? 'connect' : 'notFound'
        })
        if (localExtraStream) {
          this.monitoringVideo('localExtra')
        }
      }

    } catch (e) {
      console.warn("Failed to get local stream:", e);
      // Если стрим не удалось получить, ставим хотя бы заглушку (transceiver)
      this.pc?.addTransceiver("video");
    }
  }

  restartRTCPeer = debounce(async () => {
    console.log('try restartRTCPeer');
    if (this.restartPending) return

    const localMain = this.get('localMain')
    const localExtra = this.get('localExtra')
    const remoteMain = this.get('remoteMain')
    const remoteExtra = this.get('remoteExtra')

    const isActiveLocalMainVideo = this.isActiveVideo('localMain')
    const isActiveLocalExtraVideo = localExtra.status !== 'notFound' ? this.isActiveVideo('localExtra') : localExtra.status === 'unprepared'
    const isActiveRemoteMainVideo = this.isActiveVideo('remoteMain')
    const isActiveRemoteExtraVideo = remoteExtra.status !== 'notFound' ? this.isActiveVideo('remoteExtra') : remoteExtra.status === 'unprepared'
    // console.log(localExtra.status, localExtra.status, remoteMain.status, remoteExtra.status);
    // либо мы еще в рестарте либо видео уже активно
    if (isActiveLocalMainVideo && isActiveLocalExtraVideo && isActiveRemoteMainVideo && isActiveRemoteExtraVideo) return;

    this.determinateVideoConnect(!isActiveLocalMainVideo, !isActiveLocalExtraVideo, !isActiveRemoteMainVideo, !isActiveRemoteExtraVideo)

    if (this.get('restart') >= MAX_RESTART) return

    console.log('restartRTCPeer');

    this.set('restart', this.get('restart') + 1)

    await this.createRTCPeer(localMain.$video, localExtra.$video, remoteMain.$video, remoteExtra.$video);

    // Сразу форсируем новое предложение с небольшой задержкой,
    // чтобы RTCPeerConnection успел «стартовать»
    setTimeout(() => {
      this.createVideoOffer();
    }, 300);
  }, 3000)

  determinateVideoConnect(resetLocalMain = true, resetLocalExtra = true, resetRemoteMain = true, resetRemoteExtra = true) {
    if (resetLocalMain) {
      this.resetState('localMain', true)
    }
    if (resetLocalExtra) {
      this.resetState('localExtra', true)
    }
    if (resetRemoteMain) {
      this.resetState('remoteMain')
    }
    if (resetRemoteExtra) {
      this.resetState('remoteExtra')
    }

    if (this.unsubscribeGameSocket) {
      this.unsubscribeGameSocket()
      this.unsubscribeGameSocket = null
    }

    this.pc?.close();
    this.pc = null;
    this.pendingCandidates = [];

    this.restartPending = false;
  }

  resetState(name, isLocal) {
    const scope = this.get(name)
    this.clearDisconnectTimer(name)
    clearTimeout(scope?.timerHeardBeat)
    const $video = scope?.$video ?? {}
    if ($video.current) {
      $video.current.srcObject = null;
    }
    this.set(name, {
      ...default_video_state,
      trackingMode: isLocal ? 'availability' : 'frame',
      $video
    })
  }

  trackingVideo(name, callbackInit) {
    const scope = this.get(name)
    if (scope.trackingMode === 'frame') {
      this.requestFrameVideo(name, callbackInit)
    }
    if (scope.trackingMode === 'availability') {
      this.availabilityVideo(name, callbackInit)
    }
  }

  changeTrackingMode(name, mode) {
    const {isSubscribe, trackingMode} = this.get(name)
    if (trackingMode === mode) return

    this.update(name, {
      trackingMode: mode,
      isSubscribe: false
    })
    if (isSubscribe) {
      this.monitoringVideo(name)
    }
  }

  monitoringVideo(name) {
    const scope = this.get(name)
    if (scope.isSubscribe) return;
    scope.isSubscribe = true;

    const activeCallback = () => {
      this.clearDisconnectTimer(name)
      if (this.get(name).status !== 'stable') {
        this.update(name, {status: 'stable', error: null})
      }
    }
    const failCallback = () => {
      this.startDisconnectTimer(name)
      if (this.get(name).status !== 'reconnect') {
        this.update(name, {status: 'reconnect'})
      }
    }
    this.heardBeatVideo(name, activeCallback, failCallback)
    this.trackingVideo(name, activeCallback)
  }

  requestFrameVideo(name, callbackInit) {
    const scope = this.get(name)
    if (!scope.$video.current) return
    const handlerRequestFrameVideo = (name, callbackInit) => {
      return async (now, metadata) => {
        const scope = this.get(name)
        scope.lastFrame = now;
        if (!scope.isSubscribe) return;
        if (callbackInit) callbackInit()
        requestAnimationFrame(() => {
          const scope = this.get(name)
          if (!scope.isSubscribe) return;
          if (scope.trackingMode !== 'frame') return
          scope.$video.current.requestVideoFrameCallback(handlerRequestFrameVideo(name));
        });
      }
    }
    scope.$video.current.requestVideoFrameCallback(handlerRequestFrameVideo(name, callbackInit));
  }

  availabilityVideo(name, callbackInit) {
    const scope = this.get(name)
    if (!scope.stream) return

    const videoTrack = scope.stream.getVideoTracks()[0]
    if (videoTrack.enabled) {
      scope.availabilityVideo = true
      callbackInit()
    }

// Слушаем события
    videoTrack.addEventListener('ended', () => {
      scope.availabilityVideo = false
    });

    videoTrack.addEventListener('mute', () => {
      scope.availabilityVideo = false
    });

    videoTrack.addEventListener('unmute', () => {
      scope.availabilityVideo = true
      callbackInit()
    });
    // const handlerStatsSignalVideo = (name, callbackInit) => {
    //   return async (stats) => {
    //     const scope = this.get(name)
    //     stats.forEach(report => {
    //       if (report.type === 'outbound-rtp') {
    //         console.log(name, report);
    //         //
    //       }
    //     });
    //     if (!scope.isSubscribe) return;
    //     // console.log(name);
    //     // if (callbackInit) callbackInit()
    //     requestAnimationFrame(() => {
    //       const scope = this.get(name)
    //       if (!scope.isSubscribe) return;
    //       if (scope.trackingMode !== 'signal') return
    //       this.pc.getStats(scope.stream.getVideoTracks()[0]).then(handlerStatsSignalVideo(name));
    //     })
    //   }
    // }
    // this.pc.getStats(scope.stream.getVideoTracks()[0]).then(handlerStatsSignalVideo(name, callbackInit))
  }

  heardBeatVideo(name, activeCallback, failCallback, delay = SHORT_DISCONNECT_TIMEOUT) {
    const scope = this.get(name)

    clearTimeout(scope.timerHeardBeat)

    if (!scope.isSubscribe) return;

    scope.timerHeardBeat = setTimeout(() => {
      const scope = this.get(name)
      if (!scope.isSubscribe) return;
      this.isActiveVideo(name) ? activeCallback() : failCallback()
      this.heardBeatVideo(name, activeCallback, failCallback, HEARD_BEAT_TIME);
    }, delay);
  }

  isActiveVideo(name) {
    const scope = this.get(name)
    if (scope.trackingMode === 'frame') {
      if (scope.lastFrame === 0) return false
      const now = performance.now();
      return now - scope.lastFrame <= TIME_VIDEO_GLITCH
    }
    if (scope.trackingMode === 'availability') {
      return scope.availabilityVideo
    }
    return false
  }

  startDisconnectTimer(name) {
    const scope = this.get(name)
    if (scope.timerDisconnect) return;

    // if (this.shortDisconnectTimer) return;
    console.log('START DisconnectTimer', name);

    scope.timerDisconnect = setTimeout(() => {
      this.restartRTCPeer();
    }, SHORT_DISCONNECT_TIMEOUT)
  }

  clearDisconnectTimer(name) {
    const scope = this.get(name)
    if (!scope?.timerDisconnect) return

    console.log('CLEAR DisconnectTimer', name);
    clearTimeout(scope.timerDisconnect);
    scope.timerDisconnect = null
  }

  async getUserMedia(video = true, audio = false) {
    return await window.navigator.mediaDevices.getUserMedia({
      audio: audio, video: video,
    })
  }

  async createLocalStream(name, videoParams, audioParams) {
    const localStream = await this.getUserMedia(videoParams, audioParams)
    const audioTrack = localStream.getAudioTracks()[0];
    if (audioTrack) audioTrack.enabled = !this.get('muted');
    return localStream
  }

  async createLocalExtraStreamByMain(mainDeviceId) {
    const {deviceId} = this.get('localExtra')
    let localExtraStream = await this.createLocalStream('localExtra', {
      facingMode: "user",
      deviceId: {ideal: deviceId}
    }, false);
    if (localExtraStream.getVideoTracks()[0].getSettings().deviceId === mainDeviceId) {
      const videoDevices = await this.getDevices('videoinput')
      if (videoDevices.length >= 2) {
        const currentIndex = videoDevices.findIndex(device => device.deviceId === mainDeviceId);
        const nextDevice = videoDevices[(currentIndex + 1) % videoDevices.length];
        localExtraStream = await this.createLocalStream('localExtra', {
          deviceId: {
            exact: nextDevice.deviceId,
            ideal: deviceId
          }
        }, false);
      } else {
        localExtraStream = null
      }
    }
    return localExtraStream
  }

  handlerSetRemoteTrack = ({track, transceiver, streams}) => {
    if (track.kind === 'audio') return;
    // default is audio
    // console.log('track', track.id, transceiver.mid, transceiver.sender?.label, streams[0].id);

    const sender = transceiver.sender?.label ?? (transceiver.mid % 2 ? 'localMain' : 'localExtra')

    const name = sender.replace('local', 'remote')
    const $video = this.get(name).$video.current;
    const newStream = streams[0];
    const updateRemoteProps = {
      status: 'connect'
    }

    if ($video.srcObject !== newStream) {
      updateRemoteProps.stream = newStream
      $video.srcObject = newStream;
      // $video.play().catch((err) => {
      //   console.log('Error playing remote video:', err);
      // });
    }

    // Если после получения трека мы считали, что нужно перезапускать,
    // отменяем флаг, ведь поток уже пошёл.
    this.update(name, updateRemoteProps)

    // start only video
    this.monitoringVideo(name);
    setTimeout(() => {
      this.restartPending = false;
    }, TIME_VIDEO_GLITCH)
  }

  async createVideoOffer() {
    if (!this.pc) return;
    // <-- ADDED/CHANGED: проверяем signalingState
    if (this.pc.signalingState !== "stable") {
      console.log("Skipping createOffer because signalingState:", this.pc.signalingState);
      return;
    }

    this.makingOffer = true;
    const Game = await this.GameDfd.promise();
    try {
      // Для perfect negotiation достаточно вызвать setLocalDescription({offer:…})
      // без createOffer() вручную.
      await this.pc.setLocalDescription();
      // console.log('offer');
      Game.socketSend({
        type: "call_offer", message: {
          caller: window.PLAYER_ID, payload: this.pc.localDescription,
        },
      });
    } catch (e) {
      console.error("Error in createVideoOffer:", e);
      this.restartRTCPeer();
    } finally {
      this.makingOffer = false;
    }
  }

  async handlerOfferVideo(offer) {
    if (!this.pc) return;

    const Game = await this.GameDfd.promise();

    // <-- ADDED/CHANGED: проверяем наличие коллизии (perfect negotiation)
    const offerCollision = this.makingOffer || this.pc.signalingState !== "stable";
    if (!this.polite && offerCollision) {
      console.log(">>> Collission (impolite side), rolling back localDescription");
      try {
        // Если impolite сторона уже создала локальное описание, делаем rollback
        await this.pc.setLocalDescription();
      } catch (e) {
        console.error("Rollback error:", e);
        this.restartRTCPeer();
        return;
      }
    }

    try {
      await this.pc.setRemoteDescription(offer);

      // Применяем все отложенные кандидаты (если были)
      while (this.pendingCandidates.length) {
        await this.pc.addIceCandidate(this.pendingCandidates.shift());
      }

      // Создаём answer
      await this.pc.setLocalDescription();
      // console.log('send answer');
      Game.socketSend({
        type: "call_answer", message: {
          caller: window.PLAYER_ID, payload: this.pc.localDescription,
        },
      });
    } catch (e) {
      console.error("Error in handlerOfferVideo:", e);
      this.restartRTCPeer();
    }
  }

  async handlerAnswerVideo(answer) {
    if (!this.pc) return;
    // потому что если несколько участников то хана
    if (this.pc.signalingState === 'stable') return

    try {
      await this.pc.setRemoteDescription(answer);

      // Применяем все отложенные кандидаты
      while (this.pendingCandidates.length) {
        await this.pc.addIceCandidate(this.pendingCandidates.shift());
      }
    } catch (e) {
      console.error("Error in handlerAnswerVideo:", e);
      this.restartRTCPeer();
    }
  }

  async handlerCandidateVideo(candidate) {
    if (!this.pc) return;

    // Если remoteDescription ещё не установлена, временно буферизуем
    if (!this.pc.remoteDescription || !this.pc.remoteDescription.type) {
      this.pendingCandidates.push(candidate);
      return;
    }

    try {
      await this.pc.addIceCandidate(candidate);
    } catch (e) {
      console.error("Error in handlerCandidateVideo:", e);
      this.restartRTCPeer();
    }
  }

  async checkPossibilities() {
    try {
      await navigator.mediaDevices.getUserMedia({video: true, audio: true})
    } catch (e) {
      this.update('localMain', {status: 'permissionDenied'})
      this.update('localExtra', {status: 'notFound'})
      return false
    }
    const devices = await this.getDevices('videoinput')
    this.set('devices', devices)
    // alert(JSON.stringify(navigator.mediaDevices.getSupportedConstraints()))
    if (devices.length === 0) {
      this.update('localMain', {status: 'notFound'})
      this.update('localExtra', {status: 'notFound'})
      return false
    }

    // TODO
    // if (!devices.some((device) => device.kind === "audioinput")) {
    //   console.warn("No audio input devices found");
    //   return false;
    // }
    //
    // const permMicrophone = await navigator.permissions.query({name: "microphone"});
    // console.log(permMicrophone);
    // if (permMicrophone.state !== "granted") {
    //   console.warn("Microphone permissions not granted");
    //   return false;
    // }

    const permCamera = await navigator.permissions.query({name: 'camera'});
    if (permCamera.state === 'granted') {
      return true
    }
    if (permCamera.state === 'prompt') {
      this.update('localMain', {status: 'permissionDenied'})
      this.update('localExtra', {status: devices.length >= 2 ? 'permissionDenied' : 'notFound'})
      return true
    }
    if (permCamera.state === 'denied') {
      this.update('localMain', {status: 'permissionDenied'})
      this.update('localMain', {status: devices.length >= 2 ? 'permissionDenied' : 'notFound'})
      return false
    }
    return false
  }

  async getDevices(type) {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter(device => device.kind === type);
  }

  async switchCamera(name, deviceId) {
    if (!this.pc) return;

    try {
      // Получаем список всех видеоустройств
      const videoDevices = await this.getDevices('videoinput')

      if (videoDevices.length === 0) {
        alert('No video devices found');
        return;
      }

      const selectedDevice = videoDevices.find(v => v.deviceId === deviceId);
      if (!selectedDevice) {
        alert('Selected device not found in available devices');
        return;
      }

      // Получаем новый поток с выбранным устройством
      const newStream = await this.createLocalStream(name, {deviceId: {exact: deviceId}}, false);

      this.get(name).$video.current.srcObject = newStream;

      const videoTrack = newStream.getVideoTracks()[0];
      const senders = this.pc.getSenders();
      const videoSender = senders.find(sender => {
        return sender.track && sender.track.kind === 'video'
      });
      if (videoSender) {
        await videoSender.replaceTrack(videoTrack);
      } else {
        alert('No video sender found in RTCPeerConnection');
      }

      // Сохраняем текущий аудиотрек
      const currentAudioTrack = this.get('localMain').stream.getAudioTracks()[0];

      // Создаем объединенный поток для обновления интерфейса
      const combinedStream = new MediaStream([videoTrack]);
      if (currentAudioTrack) {
        combinedStream.addTrack(currentAudioTrack);
      }

      // Обновляем интерфейс
      this.get(name).$video.current.srcObject = combinedStream;
      this.get(name).stream.getTracks().forEach(track => {
        if (track.kind === 'video') track.stop(); // Останавливаем только старый видеотрек
      });
      this.update(name, {
        stream: combinedStream,
      });

    } catch (error) {
      console.error('Error switching camera:', error);
      this.restartRTCPeer()
    }
  }

  async mutedLocalTrack(muted) {
    this.set('muted', muted)
    const scope = this.get('localMain');
    const audioTrack = scope.stream?.getAudioTracks()[0];
    if (!audioTrack) {
      console.warn('No audio track found in the local stream!');
      return;
    }

    audioTrack.enabled = !muted;
  }
}

export default _video;
